/**
 * TerritorySelector - Sélecteur de territoire professionnel avec groupements
 * Affichage par région avec drapeaux SVG et design moderne
 */
import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, MapPin, Check, Globe } from 'lucide-react';
import { FLAGS_SVG } from './PhoneInputWithFlagSVG';

// Données des territoires groupés
const TERRITORIES = {
  antilles: {
    name: 'Antilles',
    icon: '🌴',
    color: 'from-emerald-500 to-teal-600',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-200',
    textColor: 'text-emerald-700',
    territories: [
      { code: 'GP', name: 'Guadeloupe', capital: 'Basse-Terre', population: '400 000' },
      { code: 'MQ', name: 'Martinique', capital: 'Fort-de-France', population: '375 000' },
      { code: 'SX', name: 'Saint-Martin', capital: 'Marigot', population: '35 000' },
      { code: 'BL', name: 'Saint-Barthélemy', capital: 'Gustavia', population: '10 000' }
    ]
  },
  guyane: {
    name: 'Amérique du Sud',
    icon: '🌿',
    color: 'from-green-500 to-emerald-600',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    textColor: 'text-green-700',
    territories: [
      { code: 'GF', name: 'Guyane', capital: 'Cayenne', population: '290 000' }
    ]
  },
  ocean_indien: {
    name: 'Océan Indien',
    icon: '🌊',
    color: 'from-blue-500 to-indigo-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    textColor: 'text-blue-700',
    territories: [
      { code: 'RE', name: 'La Réunion', capital: 'Saint-Denis', population: '860 000' },
      { code: 'YT', name: 'Mayotte', capital: 'Mamoudzou', population: '320 000' }
    ]
  },
  pacifique: {
    name: 'Pacifique',
    icon: '🏝️',
    color: 'from-cyan-500 to-blue-600',
    bgColor: 'bg-cyan-50',
    borderColor: 'border-cyan-200',
    textColor: 'text-cyan-700',
    territories: [
      { code: 'NC', name: 'Nouvelle-Calédonie', capital: 'Nouméa', population: '270 000' },
      { code: 'PF', name: 'Polynésie française', capital: 'Papeete', population: '280 000' }
    ]
  },
  metropole: {
    name: 'Métropole',
    icon: '🇫🇷',
    color: 'from-indigo-500 to-purple-600',
    bgColor: 'bg-indigo-50',
    borderColor: 'border-indigo-200',
    textColor: 'text-indigo-700',
    territories: [
      { code: 'FR', name: 'France métropolitaine', capital: 'Paris', population: '65 000 000' }
    ]
  }
};

// Composant Flag SVG
const FlagSVG = ({ code, size = 'normal' }) => {
  const FlagComponent = FLAGS_SVG[code];
  const sizeClass = size === 'large' ? 'w-10 h-6' : 'w-6 h-4';
  return FlagComponent ? (
    <div className={`${sizeClass} rounded overflow-hidden shadow-sm`}>
      <FlagComponent />
    </div>
  ) : (
    <div className={`${sizeClass} bg-gray-300 rounded`} />
  );
};

// Composant Territory Card
const TerritoryCard = ({ territory, region, isSelected, onClick }) => (
  <button
    type="button"
    onClick={() => onClick(territory.name)}
    className={`
      w-full flex items-center gap-3 p-3 rounded-xl transition-all duration-200
      ${isSelected 
        ? `${region.bgColor} ${region.borderColor} border-2 shadow-md` 
        : 'bg-white border border-gray-200 hover:border-gray-300 hover:shadow-sm'
      }
    `}
  >
    <FlagSVG code={territory.code} size="large" />
    <div className="flex-1 text-left">
      <p className={`font-semibold ${isSelected ? region.textColor : 'text-gray-900'}`}>
        {territory.name}
      </p>
      <p className="text-xs text-gray-500">
        {territory.capital} • {territory.population} hab.
      </p>
    </div>
    {isSelected && (
      <div className={`w-6 h-6 rounded-full bg-gradient-to-r ${region.color} flex items-center justify-center`}>
        <Check className="w-4 h-4 text-white" />
      </div>
    )}
  </button>
);

export default function TerritorySelector({
  value = '',
  onChange,
  label = 'Territoire',
  required = false,
  placeholder = 'Sélectionnez votre territoire',
  showDetails = true,
  className = ''
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Trouver le territoire sélectionné et sa région
  const findTerritory = (name) => {
    for (const [regionKey, region] of Object.entries(TERRITORIES)) {
      const territory = region.territories.find(t => t.name === name);
      if (territory) {
        return { territory, region, regionKey };
      }
    }
    return null;
  };

  const selected = findTerritory(value);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (territoryName) => {
    onChange && onChange(territoryName);
    setIsOpen(false);
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef} data-testid="territory-selector">
      {label && (
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          <MapPin className="w-4 h-4 inline-block mr-1 text-amber-500" />
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}

      {/* Bouton de sélection */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`
          w-full flex items-center justify-between gap-3 p-4 rounded-xl
          border-2 transition-all duration-200 bg-white
          ${isOpen ? 'border-amber-500 ring-2 ring-amber-500/20 shadow-lg' : 'border-gray-200 hover:border-gray-300 shadow-sm hover:shadow-md'}
        `}
      >
        {selected ? (
          <div className="flex items-center gap-3">
            <FlagSVG code={selected.territory.code} size="large" />
            <div className="text-left">
              <p className="font-semibold text-gray-900">{selected.territory.name}</p>
              {showDetails && (
                <p className="text-xs text-gray-500">
                  {selected.region.icon} {selected.region.name}
                </p>
              )}
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <div className="w-10 h-6 bg-gray-100 rounded flex items-center justify-center">
              <Globe className="w-4 h-4 text-gray-400" />
            </div>
            <span className="text-gray-400">{placeholder}</span>
          </div>
        )}
        <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 top-full left-0 right-0 mt-2 bg-white border-2 border-gray-200 rounded-2xl shadow-2xl max-h-[500px] overflow-hidden">
          <div className="overflow-y-auto max-h-[500px]">
            {Object.entries(TERRITORIES).map(([regionKey, region]) => (
              <div key={regionKey} className="border-b border-gray-100 last:border-b-0">
                {/* Header de région */}
                <div className={`sticky top-0 z-10 px-4 py-3 bg-gradient-to-r ${region.color} text-white font-bold flex items-center gap-2`}>
                  <span className="text-lg">{region.icon}</span>
                  <span>{region.name}</span>
                  <span className="ml-auto text-xs opacity-75">
                    {region.territories.length} territoire{region.territories.length > 1 ? 's' : ''}
                  </span>
                </div>
                
                {/* Territoires de la région */}
                <div className="p-3 grid gap-2">
                  {region.territories.map((territory) => (
                    <TerritoryCard
                      key={territory.code}
                      territory={territory}
                      region={region}
                      isSelected={value === territory.name}
                      onClick={handleSelect}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Info territoire sélectionné */}
      {selected && showDetails && (
        <div className={`mt-3 p-3 rounded-xl ${selected.region.bgColor} ${selected.region.borderColor} border`}>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg bg-gradient-to-r ${selected.region.color} flex items-center justify-center text-white text-lg`}>
              {selected.region.icon}
            </div>
            <div>
              <p className={`font-semibold ${selected.region.textColor}`}>{selected.territory.name}</p>
              <p className="text-xs text-gray-600">
                Capitale: {selected.territory.capital} • Population: {selected.territory.population} habitants
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Export des données
export { TERRITORIES };
